import * as THREE from 'three';

export class RaycastInteraction {
  private raycaster = new THREE.Raycaster();
  private pointer = new THREE.Vector2();
  private hovered: THREE.Object3D | null = null;
  private originalEmissive = new Map<THREE.Material, THREE.Color>();

  constructor(
    private canvas: HTMLCanvasElement,
    private camera: THREE.Camera,
    private scene: THREE.Scene,
    private onHover: (object: THREE.Object3D | null, x: number, y: number) => void,
    private onSelect: (object: THREE.Object3D) => void
  ) {
    canvas.addEventListener('pointermove', this.move);
    canvas.addEventListener('pointerleave', this.leave);
    canvas.addEventListener('click', this.click);
  }

  private hit(event: PointerEvent): THREE.Object3D | null {
    const rect = this.canvas.getBoundingClientRect();
    this.pointer.set(((event.clientX - rect.left) / rect.width) * 2 - 1, -((event.clientY - rect.top) / rect.height) * 2 + 1);
    this.raycaster.setFromCamera(this.pointer, this.camera);
    for (const hit of this.raycaster.intersectObjects(this.scene.children, true)) {
      const root = hit.object.userData.interactiveRoot as THREE.Object3D | undefined;
      if (root) return root;
    }
    return null;
  }

  private move = (event: PointerEvent): void => {
    const next = this.hit(event);
    if (next !== this.hovered) {
      this.restore();
      this.hovered = next;
      if (next) this.highlight(next);
    }
    this.canvas.style.cursor = next ? 'pointer' : 'grab';
    this.onHover(next, event.clientX, event.clientY);
  };

  private click = (event: PointerEvent): void => {
    const target = this.hit(event);
    if (target) this.onSelect(target);
  };

  private leave = (): void => {
    this.restore();
    this.onHover(null, 0, 0);
  };

  private highlight(root: THREE.Object3D): void {
    root.traverse((object) => {
      if (!(object instanceof THREE.Mesh)) return;
      const materials = Array.isArray(object.material) ? object.material : [object.material];
      materials.forEach((material) => {
        if ('emissive' in material && material.emissive instanceof THREE.Color) {
          this.originalEmissive.set(material, material.emissive.clone());
          material.emissive.lerp(new THREE.Color(0x36d9ff), 0.46);
        }
      });
    });
  }

  private restore(): void {
    this.originalEmissive.forEach((color, material) => {
      if ('emissive' in material && material.emissive instanceof THREE.Color) material.emissive.copy(color);
    });
    this.originalEmissive.clear();
    this.hovered = null;
  }

  dispose(): void {
    this.canvas.removeEventListener('pointermove', this.move);
    this.canvas.removeEventListener('pointerleave', this.leave);
    this.canvas.removeEventListener('click', this.click);
  }
}
